import { useEffect, useState } from 'react';
import { PhoneOff, ShieldCheck, Trash2, RefreshCcw, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { flaggedCallersAPI } from '../services/api.js';
import { formatPhone, formatDateTimeWithZone } from '../lib/format.js';

// BLOCKED & FLAGGED CALLERS — the admin review this subsystem was built to need.
//
// The panel warns and ends a call it can't hear, then reports the incident. Policy
// (flaggedCallers.js): 1st incident → `flagged` (they can still call back);
// 2nd → `blocked`, and the panel REJECTS them at call-start "pending admin review".
//
// That review had no screen. So a real customer with two bad-audio calls — a weak
// cell, a hands-free kit, a noisy cab — was silently barred from a business's line,
// with nobody able to see it had happened or undo it. Same shape as a campaign
// auto-paused with no banner: the platform takes an action and nothing says so.
//
// `cleared` deliberately resets incidentCount server-side, so clearing someone gives
// them the FULL warn-then-block cycle again rather than one grace call before an
// instant re-block. The copy says that, because it changes whether an admin trusts
// the button.

const STATUS = {
  blocked: {
    label: 'Blocked',
    cls: 'bg-rose-950/60 text-rose-300',
    lead: 'This number is being rejected at call-start.'
  },
  flagged: {
    label: 'Flagged',
    cls: 'bg-amber-950/60 text-amber-300',
    lead: 'Warned once. One more bad-audio call and they will be blocked automatically.'
  },
  cleared: {
    label: 'Cleared',
    cls: 'bg-emerald-950/60 text-emerald-300',
    lead: 'Cleared by an admin — calling normally.'
  }
};

export default function FlaggedCallersEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState({});
  const [notes, setNotes] = useState({});

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await flaggedCallersAPI.list();
      setItems(r?.data || []);
    } catch (err) {
      // 404 = core doesn't have the routes deployed; not an error worth shouting.
      if (err.status !== 404) setError(err.message || 'Failed to load flagged callers.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const review = async (row, status) => {
    setBusyId(row.id);
    setError('');
    try {
      const payload = { status };
      const note = (notes[row.id] ?? row.reviewNote ?? '').trim();
      if (note !== (row.reviewNote || '')) payload.reviewNote = note || null;
      const r = await flaggedCallersAPI.review(row.id, payload);
      const updated = r?.data || r;
      setItems((list) => list.map((i) => (i.id === row.id ? updated : i)));
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not update this caller.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete the record for ${formatPhone(row.phoneE164)}? They start fresh — the next bad-audio call counts as their first.`
    )) return;
    setBusyId(row.id);
    try {
      await flaggedCallersAPI.remove(row.id);
      setItems((list) => list.filter((i) => i.id !== row.id));
    } catch (err) {
      setError(err.body?.message || err.message || 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  const blockedCount = items.filter((i) => i.status === 'blocked').length;

  return (
    <section className="card">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PhoneOff size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Blocked &amp; flagged callers</h2>
        </div>
        <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
          <RefreshCcw size={15} /> Refresh
        </button>
      </header>

      <p className="mb-4 text-sm text-stone-400">
        When Alfred can&rsquo;t hear a caller at all, it says so and ends the call. A second
        bad-audio call from the same number blocks it automatically, and Alfred stops answering
        them. Review those here — bad audio is often a weak signal or a hands-free kit, not a
        problem caller.
      </p>

      {blockedCount > 0 && (
        <p className="mb-4 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-950/30 px-4 py-3 text-sm text-rose-200">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>
            <strong>{blockedCount}</strong> number{blockedCount === 1 ? ' is' : 's are'} currently
            being turned away. If any of them are customers, clear them.
          </span>
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">
          No callers have been flagged. Nothing is being turned away.
        </p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((row) => {
            const s = STATUS[row.status] || { label: row.status, cls: 'bg-stone-800 text-stone-300', lead: '' };
            const isOpen = !!open[row.id];
            const busy = busyId === row.id;
            return (
              <li key={row.id} className="py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-stone-100">{formatPhone(row.phoneE164)}</span>
                      <span className={`badge ${s.cls}`}>{s.label}</span>
                      <span className="text-xs text-stone-500">
                        {row.incidentCount} bad-audio call{row.incidentCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-stone-400">{s.lead}</div>
                    <div className="mt-1 text-xs text-stone-500">
                      Last heard {formatDateTimeWithZone(row.lastSeenAt)}
                      {row.lastDid ? ` · on ${formatPhone(row.lastDid)}` : ''}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-1">
                    {row.status !== 'cleared' && (
                      <button type="button" className="btn-ghost" onClick={() => review(row, 'cleared')} disabled={busy}>
                        <ShieldCheck size={14} /> Let them call
                      </button>
                    )}
                    {row.status !== 'blocked' && (
                      <button type="button" className="btn-ghost" onClick={() => review(row, 'blocked')} disabled={busy}>
                        <PhoneOff size={14} /> Block
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-ghost text-red-300 hover:text-red-200"
                      onClick={() => remove(row)}
                      disabled={busy}
                      aria-label="Delete record"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-2 flex items-center gap-1 text-xs text-stone-400 hover:text-alfred-gold"
                  onClick={() => setOpen((o) => ({ ...o, [row.id]: !o[row.id] }))}
                >
                  {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  Details and review note
                </button>

                {isOpen && (
                  <div className="mt-2 space-y-3 rounded-lg border border-stone-800 bg-stone-950/40 p-3">
                    {row.lastReason && (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-stone-500">
                          What Alfred heard on the last call
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-stone-300 break-words">
                          {row.lastReason}
                        </div>
                      </div>
                    )}
                    {row.lastCallId && (
                      <Link to={`/calls/${row.lastCallId}`} className="inline-block text-xs text-alfred-gold hover:underline">
                        Open that call →
                      </Link>
                    )}
                    {row.reviewedAt && (
                      <div className="text-xs text-stone-500">
                        Last reviewed {formatDateTimeWithZone(row.reviewedAt)}
                        {row.reviewedBy ? ` by ${row.reviewedBy}` : ''}
                      </div>
                    )}
                    <div>
                      <label className="field-label text-xs">Review note</label>
                      <textarea
                        className="field-input min-h-[60px] text-sm"
                        placeholder="Why you cleared or blocked this number — e.g. 'regular customer, calls from a truck'."
                        value={notes[row.id] ?? row.reviewNote ?? ''}
                        onChange={(e) => setNotes((n) => ({ ...n, [row.id]: e.target.value }))}
                        maxLength={2000}
                      />
                      <p className="mt-1 text-xs text-stone-500">
                        Saved with your next Let-them-call or Block action.
                      </p>
                    </div>
                    {row.status === 'blocked' && (
                      <p className="text-xs text-stone-400">
                        Clearing this number resets its count, so they get the full
                        warn-then-block cycle again rather than one call before an instant re-block.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
